import React, { useState, useEffect, useRef, useCallback } from "react";
import Carousel from "./Carousel";
import { MediaItem, fetchMediaData } from "../services/apiService";

interface CarouselItem {
  media_url: string;
  resizedImageUrl?: string[];
  mediaType: string;
  timestamp: string;
}

const ITEMS_PER_PAGE = 9;

const PhotoGallery: React.FC = () => {
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  const observer = useRef<IntersectionObserver | null>(null);
  const lastElementRef = useCallback(
    (node: HTMLElement | null) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && currentPage < totalPages) {
          setCurrentPage((prevPage) => prevPage + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, currentPage, totalPages]
  );

  useEffect(() => {
    const loadMediaData = async () => {
      try {
        setIsLoading(true);
        const { items, total } = await fetchMediaData(currentPage, ITEMS_PER_PAGE);
  
        setMediaItems((prevItems) => [...prevItems, ...items]);
        setTotalItems(total);
        setIsLoading(false);
      } catch (err) {
        setError('Error fetching metadata');
        setIsLoading(false);
      }
    };

    loadMediaData();
  }, [currentPage]);


  const getResizedImageUrls = (media: MediaItem): string[] => {
    if (Array.isArray(media.resizedImageUrl)) {
      return media.resizedImageUrl;
    } else if (typeof media.resizedImageUrl === 'string') {
      // If it's a string, assume it's the base URL and construct the array
      return [480, 768, 1200, 1600].map(size => `${media.resizedImageUrl}-${size}.webp`);
    }
    // Fallback to an empty array if neither condition is met
    return [];
  };

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
        {mediaItems.map((media, index) => (
          <div
            key={media.id}
            ref={index === mediaItems.length - 1 ? lastElementRef : null}
            className="relative rounded-lg shadow-lg overflow-hidden"
          >
            <div className="absolute top-0 left-0 bg-black bg-opacity-60 text-white p-2 text-xs z-10">
              {(() => {
                const now = new Date();
                const timestamp = new Date(media.timestamp);
                const diffTime = now.getTime() - timestamp.getTime();
                const diffMinutes = Math.floor(diffTime / (1000 * 60));
                const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
                const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                const diffYears = Math.floor(diffDays / 365);

                if (diffYears > 0) {
                  return new Intl.RelativeTimeFormat().format(-diffYears, 'year');
                } else if (diffDays > 0) {
                  return new Intl.RelativeTimeFormat().format(-diffDays, 'day');
                } else if (diffHours > 0) {
                  return new Intl.RelativeTimeFormat().format(-diffHours, 'hour');
                } else {
                  return new Intl.RelativeTimeFormat().format(-diffMinutes, 'minute');
                }
              })()}
            </div>

            {media.media_type === "CAROUSEL_ALBUM" && media.children?.length ? (
              <Carousel
                mediaItems={media.children.map((child): CarouselItem => ({
                  media_url: child.media_url,
                  resizedImageUrl: getResizedImageUrls(media).filter(url => url.includes(child.id)),
                  mediaType: child.media_type,
                  timestamp: media.timestamp,
                }))}
                caption={media.caption}
                permalink={media.permalink}
              />
            ) : media.media_type === "IMAGE" ? (
              <img
                src={getResizedImageUrls(media)[0] || ''}
                srcSet={getResizedImageUrls(media).map((url, index) => `${url} ${[480, 768, 1200, 1600][index]}w`).join(', ')}
                sizes="(max-width: 480px) 480px, (max-width: 768px) 768px, (max-width: 1200px) 1200px, 1600px"
                alt={media.caption}
                className="w-full h-auto object-cover"
              />
            ) : (
              <p>No media available</p>
            )}

            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white p-2 text-sm font-roboto-condensed truncate">
              <a
                href={media.permalink}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                {media.caption || "No caption provided"}
              </a>
            </div>
          </div>
        ))}
      </div>

      {isLoading && <p>Loading more photos...</p>}
      {error && <p>Error fetching photos: {error}</p>}
    </div>
  );
};

export default PhotoGallery;
